import 'css/src/components/common/video-preview/VideoWithScrubber';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import type Immutable from 'immutable';
import type Luminary from '@lumen5/luminary';

import type { VideoRender } from 'js/src/client';
import GenericBasePreviewElement from 'js/src/components/common/video-preview/GenericBasePreviewElement';
import SlideMarkerScrubber from 'js/src/components/common/video-preview/SlideMarkerScrubber';
import { PLAYBACK_STATE } from 'js/src/components/common/video-preview/VideoPreview';
import type { ImmutableMap } from 'js/src/libs/consts';
import Timer from 'js/src/libs/timer';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';
import {
    luminaryReduxConnect,
    sceneGroupDurationsWithoutTransitions,
    sceneGroupStartTimes,
    slideEndTimes,
    slideIdsByStartTime,
    slideStartTimes,
} from 'js/src/luminary';
import type { CacheRef } from 'js/src/luminary/luminary-redux-connect-types';

const getSeekTimeOffset = (
    new_time: number,
    slideStartTimes: number[],
    sceneDurations: number[],
) => {
    // find the scene with start time >= new_time
    let segmentIdx = 0;
    let seekTime = 0;

    slideStartTimes.find((startTime, idx) => {
        const endTime = startTime + sceneDurations[idx];
        if (new_time >= startTime && new_time < endTime) {
            // matches this idx
            segmentIdx = idx; // we want the one before
            seekTime = new_time - slideStartTimes[idx]; // in ms
            return true;
        }
        return false;
    });
    return [segmentIdx, seekTime];
};

type VideoWithScrubberOwnProps = {
    autoplay?: boolean;
    sortedVideoSourceList: Immutable.List<ImmutableMap<VideoRender>>;
    onSeek?: (newTime: number) => void;
    numOpenCommentsPerScene?: Immutable.Map<string, number>;
    commentMarkerCallback?: (time: number, forceCommentingMode: boolean) => void;
    playbackState: PLAYBACK_STATE;
    setPlaybackState: (newPlaybackState: PLAYBACK_STATE, callback?: () => void) => void;
    muted: boolean;
    onCurrentTimeUpdate?: (currentTime: number) => void;
};
type VideoWithScrubberPropsFromRedux = ConnectedProps<typeof VideoWithScrubberConnector>;
type VideoWithScrubberState = {
    current_time: number;
    current_transition_duration: number;
    showCommentMarker: boolean;
    currentSegmentIdx: number;
    videoUrl: string | undefined;
};

export class VideoWithScrubberPresentation extends GenericBasePreviewElement<
    VideoWithScrubberOwnProps & VideoWithScrubberPropsFromRedux,
    VideoWithScrubberState
> {
    progressBarRef: React.RefObject<HTMLSpanElement>;
    publishedVideoRef: React.RefObject<HTMLVideoElement>;
    _clock_timeout: Timer | null;

    constructor(props: VideoWithScrubberOwnProps & VideoWithScrubberPropsFromRedux) {
        super(props);

        // react refs
        this.progressBarRef = React.createRef();
        this.publishedVideoRef = React.createRef();

        this._clock_timeout = null;
        this.state = {
            current_time: 0,
            current_transition_duration: 0,
            showCommentMarker: false,
            currentSegmentIdx: 0,
            videoUrl: this.props.sortedVideoSourceList.get(0)?.get('https_url'),
        };

        // function binding
        this.seek = this.seek.bind(this);
        this.togglePlay = this.togglePlay.bind(this);
        this.playVideoSegment = this.playVideoSegment.bind(this);
    }

    override componentDidUpdate(
        prevProps: VideoWithScrubberOwnProps & VideoWithScrubberPropsFromRedux,
    ) {
        if (this.publishedVideoRef.current) {
            // Remove the 'ended' event listener
            this.publishedVideoRef.current.removeEventListener('ended', this.playVideoSegment);
            // Add the 'ended' event listener
            this.publishedVideoRef.current.addEventListener('ended', this.playVideoSegment);
        }

        /*
            when rendering goes from queued -> published for the first time, we first SET_VIDEO_STATUS to published,
            which causes this component to mount. so we need to listen for when the https_url is available in STORE_VIDEOS
            action and set the right url.
        */

        if (
            prevProps.sortedVideoSourceList.getIn([this.state.currentSegmentIdx, 'https_url']) !==
            this.props.sortedVideoSourceList.getIn([this.state.currentSegmentIdx, 'https_url'])
        ) {
            this.setState({
                videoUrl: this.props.sortedVideoSourceList.getIn([
                    this.state.currentSegmentIdx,
                    'https_url',
                ]) as string,
            });
        }
    }

    override componentWillUnmount() {
        // interrupt & cancel any animations, clocks, etc
        if (this._clock_timeout !== null) {
            this._clock_timeout.cancel();
            this._clock_timeout = null;
        }
    }

    async _setCurrentTime(current_time: number, otherState = {}) {
        return new Promise<void>((resolve) => {
            this.setState(
                {
                    current_time,
                    ...otherState,
                },
                () => {
                    if (typeof this.props.onCurrentTimeUpdate === 'function') {
                        this.props.onCurrentTimeUpdate(current_time);
                    }
                    resolve();
                },
            );
        });
    }

    _setTimer() {
        const UPDATE_INTERVAL = 100;
        this._clock_timeout = new Timer(() => {
            if (!this.publishedVideoRef.current) throw new Error('Video element not found');
            let current_time = this.publishedVideoRef.current.currentTime * 1000;
            if (this.state.currentSegmentIdx > 0) {
                const sceneGroupStartTime =
                    this.props.sceneGroupStartTimes[this.state.currentSegmentIdx];
                current_time += sceneGroupStartTime;
            }
            this._setCurrentTime(current_time);
            if (this.props.playbackState === PLAYBACK_STATE.PLAYING) {
                this._setTimer();
            }
        }, UPDATE_INTERVAL);
    }

    playClock() {
        if (this._clock_timeout) {
            this._clock_timeout.resume();
        } else {
            this._setTimer();
        }
    }

    stopClock() {
        if (this._clock_timeout !== null) {
            this._clock_timeout.cancel();
            this._clock_timeout = null;
        }
        this._setCurrentTime(0);
    }

    pauseClock() {
        if (this._clock_timeout !== null) {
            this._clock_timeout.pause();
        }
    }

    getProgressBarPercentage(total_runtime: number) {
        return (this.state.current_time * 100.0) / total_runtime;
    }

    /**
     * updates the progress bar for a seeked time
     * @private
     */
    async _instantSeekProgressBar(new_time: number, transition_duration = 0) {
        if (this.progressBarRef.current) {
            this.progressBarRef.current.style.transitionDuration = '0.1s';
        }

        await this._setCurrentTime(new_time, {
            current_transition_duration: transition_duration,
        });

        if (this.props.playbackState === PLAYBACK_STATE.PLAYING) {
            this.playClock();
        }
    }

    handleSeekForStoryVideos(new_time: number) {
        const [segmentIdx, seekTime] = getSeekTimeOffset(
            new_time,
            this.props.sceneGroupStartTimes,
            this.props.sceneGroupDurationsWithoutTransitions,
        );
        this.setState(
            {
                currentSegmentIdx: segmentIdx,
                videoUrl: this.props.sortedVideoSourceList.getIn([
                    segmentIdx,
                    'https_url',
                ]) as string,
            },
            () => {
                if (!this.publishedVideoRef.current) throw new Error('Video element not found');
                this.publishedVideoRef.current.currentTime = seekTime / 1000;
                this.props.playbackState === PLAYBACK_STATE.PLAYING
                    ? this.publishedVideoRef.current.play()
                    : this.publishedVideoRef.current.pause();
            },
        );
    }

    seek(new_time: number, transition_duration = 0) {
        this._instantSeekProgressBar(new_time, transition_duration);

        typeof this.props.onSeek === 'function' && this.props.onSeek(new_time);

        if (this.props.sortedVideoSourceList.size > 1) {
            this.handleSeekForStoryVideos(new_time);
        } else {
            if (!this.publishedVideoRef.current) throw new Error('Video element not found');
            this.publishedVideoRef.current.currentTime = new_time / 1000;
        }
    }

    /* ANIMATION METHODS */
    override play() {
        /* can be called by parent */
        return new Promise<void>((resolve) => {
            // If replaying from stopped state, reset to first segment
            if (this.props.playbackState === PLAYBACK_STATE.STOPPED) {
                this.setState(
                    {
                        currentSegmentIdx: 0,
                        videoUrl: this.props.sortedVideoSourceList.getIn([
                            0,
                            'https_url',
                        ]) as string,
                    },
                    () => {
                        this.props.setPlaybackState(PLAYBACK_STATE.PLAYING, () => {
                            this.playClock();
                            this.playVideo();
                            resolve();
                        });
                    },
                );
            } else {
                this.props.setPlaybackState(PLAYBACK_STATE.PLAYING, () => {
                    this.playClock();
                    this.playVideo();
                    resolve();
                });
            }
        });
    }

    override pause() {
        /* can be called by parent */
        return new Promise<void>((resolve) => {
            this.props.setPlaybackState(PLAYBACK_STATE.PAUSED, () => {
                this.pauseClock();
                this.pauseVideo();
                resolve();
            });
        });
    }

    override stop() {
        /* can be called by parent */
        return new Promise<void>((resolve) => {
            this._setCurrentTime(0).then(() => {
                this.props.setPlaybackState(PLAYBACK_STATE.STOPPED, () => {
                    this.stopClock();
                    this.pauseVideo();
                    resolve();
                });
            });
        });
    }

    playVideo() {
        if (this.publishedVideoRef && this.publishedVideoRef.current) {
            this.publishedVideoRef.current.play();
        }
    }

    pauseVideo() {
        if (this.publishedVideoRef && this.publishedVideoRef.current) {
            this.publishedVideoRef.current.pause();
        }
    }

    togglePlay() {
        if (this.props.playbackState !== PLAYBACK_STATE.PLAYING) {
            this.play();
        } else {
            this.pause();
        }
    }

    playVideoSegment() {
        const nextSegmentIdx = this.state.currentSegmentIdx + 1;
        if (nextSegmentIdx >= this.props.sortedVideoSourceList.size) {
            // should replay video
            this.props.setPlaybackState(PLAYBACK_STATE.STOPPED, () => {
                this.stopClock();
            });
        } else {
            this.setState(
                {
                    currentSegmentIdx: nextSegmentIdx,
                    videoUrl: this.props.sortedVideoSourceList.getIn([
                        nextSegmentIdx,
                        'https_url',
                    ]) as string,
                },
                () => {
                    this.props.setPlaybackState(PLAYBACK_STATE.PLAYING, () => {
                        if (!this.publishedVideoRef.current)
                            throw new Error('Video element not found');
                        this.publishedVideoRef.current.play();
                    });
                },
            );
        }
    }

    override render() {
        return (
            <div className="col-md-12 video-with-scrubber">
                <div className="slide-corner-radius slide-shadow">
                    <div className="video-preview position-relative mx-l5-auto my-l5-0 w-100">
                        <div
                            onClick={this.togglePlay}
                            className={`luminary-canvas top-corner-radius ${this.props.aspectRatio}`}
                        >
                            <video
                                className="w-100 luminary-preview"
                                key={this.state.videoUrl}
                                ref={this.publishedVideoRef}
                                muted={this.props.muted}
                                autoPlay={this.props.autoplay}
                                poster={
                                    this.state.currentSegmentIdx === 0
                                        ? (this.props.sortedVideoSourceList.getIn([
                                              0,
                                              'placeholder_image_url',
                                          ]) as string)
                                        : undefined
                                }
                                playsInline
                                disablePictureInPicture
                                disableRemotePlayback
                            >
                                <source src={this.state.videoUrl} />
                            </video>
                        </div>
                    </div>

                    <div className="d-flex align-items-center slide-meta">
                        <div className="progress slide-meta-overlay">
                            <span
                                ref={this.progressBarRef}
                                className="progress-bar progress-bar-grey progress-bar-second-transitions w-100"
                                style={{
                                    transform: `scaleX(${this.getProgressBarPercentage(this.props.duration) / 100})`,
                                }}
                            />
                        </div>
                        <SlideMarkerScrubber
                            slideStartTimes={this.props.slideStartTimes}
                            slideEndTimes={this.props.slideEndTimes}
                            slideIdsByStartTime={this.props.slideIdsByStartTime}
                            numOpenCommentsPerScene={this.props.numOpenCommentsPerScene}
                            duration={this.props.duration}
                            seek={this.seek}
                            commentMarkerCallback={this.props.commentMarkerCallback}
                        />
                        <div className="position-absolute start-0 ms-l5-8 pe-none blur-text-bkg">
                            {getTimeStringFromSeconds(this.state.current_time / 1000)}
                        </div>
                        <div className="position-absolute end-0 me-l5-8 pe-none blur-text-bkg">
                            {getTimeStringFromSeconds(this.props.duration / 1000)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapLuminaryStateToProps = (
    luminary: Luminary,
    ownProps: VideoWithScrubberOwnProps,
    cacheRef: CacheRef,
) => {
    return {
        duration: luminary.duration,
        aspectRatio: luminary.aspectRatio,
        slideStartTimes: cacheRef('slideStartTimes', slideStartTimes(luminary)),
        sceneGroupDurationsWithoutTransitions: cacheRef(
            'sceneGroupDurationsWithoutTransitions',
            sceneGroupDurationsWithoutTransitions(luminary),
        ),
        sceneGroupStartTimes: cacheRef('sceneGroupStartTimes', sceneGroupStartTimes(luminary)),
        slideEndTimes: cacheRef('slideEndTimes', slideEndTimes(luminary)),
        slideIdsByStartTime: cacheRef('slideIdsByStartTime', slideIdsByStartTime(luminary)),
    };
};
const VideoWithScrubberConnector = luminaryReduxConnect(
    undefined,
    undefined,
    mapLuminaryStateToProps,
    undefined,
    {
        forwardRef: true,
    },
);

const VideoWithScrubber = VideoWithScrubberConnector(VideoWithScrubberPresentation);

export default VideoWithScrubber;
